import { ClientLoggingService } from '../../../shared/client-logging.service';
import { Component, DestroyRef, OnInit, Output, EventEmitter, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChatService } from '../../../services/chat.service';
import { ChatSession } from '../../../models/chat.models';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';

/** Scrollable list of past chat sessions displayed in the chat side-panel, supporting session switching and deletion. */
@Component({
  selector: 'app-chat-history-list',
  imports: [
    CommonModule,
    MatButtonModule,
    MatListModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
    DatePipe,
  ],
  templateUrl: './chat-history-list.component.html',
  styleUrls: ['./chat-history-list.component.scss'],
})
export class ChatHistoryListComponent implements OnInit {
  private chatService = inject(ChatService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  @Input() currentSessionId: string | null = null;
  @Output() sessionSelected = new EventEmitter<string>();
  @Output() sessionDeleted = new EventEmitter<string>();

  sessions: ChatSession[] = [];
  loading = true;
  error: string | null = null;
  errorKind: 'authorization' | 'service' | null = null;
  private loadGeneration = 0;

  ngOnInit() {
    this.loadSessions();
  }

  loadSessions() {
    const generation = ++this.loadGeneration;
    this.loading = true;
    this.error = null;
    this.errorKind = null;
    this.sessions = [];
    this.chatService
      .listSessions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (generation !== this.loadGeneration) return;
          // Filter out empty sessions to avoid clutter
          this.sessions = (response.sessions || []).filter(s => s.messageCount > 0);
          this.error = null;
          this.loading = false;
        },
        error: err => {
          if (generation !== this.loadGeneration) return;
          ClientLoggingService.log('ERROR', 'Failed to load sessions', err);
          const status =
            typeof err === 'object' && err !== null && 'status' in err && typeof err.status === 'number'
              ? err.status
              : null;
          this.errorKind = status === 401 || status === 403 ? 'authorization' : 'service';
          this.error =
            this.errorKind === 'authorization'
              ? 'You do not have permission to load chat history.'
              : 'Failed to load chat history.';
          this.loading = false;
        },
      });
  }

  selectSession(session: ChatSession) {
    this.sessionSelected.emit(session.sessionId);
  }

  isActiveSession(session: ChatSession): boolean {
    return this.currentSessionId === session.sessionId;
  }

  deleteSession(event: Event, session: ChatSession) {
    event.stopPropagation(); // Prevent selection when clicking delete

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Chat',
        message: 'Are you sure you want to delete this chat session? This cannot be undone.',
        confirmText: 'Delete',
        confirmColor: 'warn',
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) {
          this.chatService.deleteSession(session.sessionId).subscribe({
            next: () => {
              this.sessions = this.sessions.filter(s => s.sessionId !== session.sessionId);
              this.sessionDeleted.emit(session.sessionId);
            },
            error: err => {
              ClientLoggingService.log('ERROR', 'Failed to delete session', err);
            },
          });
        }
      });
  }
}
